import { todayStr } from "../../lib/date";
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { createIncidentReport, updateIncidentReport, deleteIncidentReport } from "../../lib/api";

const VIS_ROLES = ["Admin", "Clinician", "All Staff"];

function fmt(d, t) {
  return `${d || ""}${t ? ` ${t}` : ""}`.trim() || "—";
}

const emptyForm = () => ({
  date: todayStr(),
  time: new Date().toTimeString().slice(0, 5),
  description: "",
  visibility: ["Admin"],
});

export function StudentGeneralNotesTab({ studentId, notes, userId, onUpdated }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const openAdd = () => { setEditingId(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (n) => {
    setEditingId(n.id);
    setForm({ date: n.date || "", time: n.time || "", description: n.description || "", visibility: n.visibility || ["Admin"] });
    setDialogOpen(true);
  };

  const toggleVis = (role) => setForm((p) => ({
    ...p,
    visibility: p.visibility.includes(role) ? p.visibility.filter((r) => r !== role) : [...p.visibility, role],
  }));

  const save = async () => {
    if (!form.description.trim()) { toast.error("Note is required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await updateIncidentReport(editingId, { ...form, type: "general_note" });
        toast.success("General note updated");
      } else {
        await createIncidentReport({
          student_id: studentId, type: "general_note", incident_type: "",
          date: form.date, time: form.time, description: form.description,
          visibility: form.visibility, reporter_id: userId,
        });
        toast.success("General note added");
      }
      setDialogOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save note");
    }
    setSaving(false);
  };

  const remove = async (n) => {
    if (!window.confirm("Delete this general note?")) return;
    try { await deleteIncidentReport(n.id); toast.success("Deleted"); onUpdated?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="space-y-4" data-testid="student-general-notes-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>General Notes</CardTitle>
              <CardDescription>{notes.length} note{notes.length === 1 ? "" : "s"} on file</CardDescription>
            </div>
            <Button onClick={openAdd} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="student-add-general-note-btn">+ Add General Note</Button>
          </div>
        </CardHeader>
        <CardContent>
          {notes.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No general notes for this student yet.</p>
          ) : (
            <div className="space-y-3">
              {notes.map((n) => (
                <div key={n.id} className="border rounded-md p-3 space-y-1" data-testid={`student-general-note-${n.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="text-xs text-muted-foreground">{fmt(n.date, n.time)} · {(n.visibility || ["Admin"]).join(", ")}</div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(n)} data-testid={`student-general-note-edit-${n.id}`}>Edit</Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(n)} data-testid={`student-general-note-delete-${n.id}`}>Delete</Button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">{n.description}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="general-note-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Edit General Note" : "Add General Note"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="general-note-date" /></div>
              <div className="space-y-2"><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm((p) => ({ ...p, time: e.target.value }))} data-testid="general-note-time" /></div>
            </div>
            <div className="space-y-2"><Label>Note *</Label><Textarea rows={5} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="general-note-description" /></div>
            <div className="space-y-2">
              <Label>Visibility</Label>
              <div className="flex gap-4">
                {VIS_ROLES.map((role) => (
                  <div key={role} className="flex items-center space-x-2">
                    <Checkbox id={`gn-vis-${role}`} checked={form.visibility.includes(role)} onCheckedChange={() => toggleVis(role)} />
                    <Label htmlFor={`gn-vis-${role}`} className="cursor-pointer">{role}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={save} disabled={saving} data-testid="save-general-note-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
